<script setup lang="ts">
import useUserStore from "~~/stores/user";
import useAsync from "~/hooks/use-async";
import BillingAPI from "~/services/billing";
import { formatMercadoPagoCard } from "~/services/format/billing";
import SubscriptionAPI from "~/services/subscription";
import { getExpirationDate } from "~/utils/subscription";

export interface BillingFormProps {
  outline?: boolean;
  subscription: IModelCommerce;
}

const { t } = useI18n();
const userStore = useUserStore();
const runtimeConfig = useRuntimeConfig().public;
const props = defineProps<BillingFormProps>();
const emit = defineEmits(["success", "error", "loading", "submit"]);

const onSuccess = () => emit("success");
const onError = (err: Error) => emit("error", err);
const onPayLoading = () => emit("loading");

const shouldSaveNewCard = ref(false);
const userCreditCard = computed(() => userStore.creditCard);
const isOwnCardUsed = ref(Boolean(userCreditCard.value));
const hasMercadoPagoPayment = runtimeConfig.APP_FLAGS.hasMercadoPagoPayment;

const { loading: loadingPayOwnCard, run: payWithOwnCreditCard } = useAsync(
  () => SubscriptionAPI.subscribeWithOwnCreditCard(props.subscription.id),
  { onSuccess, onError },
);

const { loading: loadingPayNewCard, run: payWithNewCard } = useAsync(
  (card: any) => {
    const paymentApi = BillingAPI.payWithMercadoPagoCreditCard;

    emit("submit", card);
    return paymentApi(props.subscription.id, card);
  },
  { onSuccess, onError },
);

const handlePayment = (card: IModelCreditCard | IModelStripeCC) => {
  const formattedCard = formatMercadoPagoCard(card as IModelCreditCard);
  return payWithNewCard(formattedCard);
};

const loading = computed(() => loadingPayOwnCard.value || loadingPayNewCard.value);

watch(loading, onPayLoading);
</script>

<template>
  <div class="mt-6">
    <div>
      <CardCredit
        v-if="userCreditCard"
        v-model:has-radio-active="isOwnCardUsed"
        has-radio
        :card="userCreditCard"
        :disabled="loading"
      />

      <div class="my-6">
        <BaseButton
          v-if="isOwnCardUsed"
          :disabled="loading"
          class="text-main text-button-bold"
          type="secondary"
          name="billing-tab"
          @click="isOwnCardUsed = false"
        >
          {{ t('button.enter_another_card') }}
        </BaseButton>

        <div v-else-if="userCreditCard" class="text-body-medium text-secondary">
          {{ t('button.enter_another_card') }}
        </div>
      </div>

      <template v-if="isOwnCardUsed">
        <AccountSubscriptionBuyDetails
          :price="props.subscription?.price"
          :currency="props.subscription?.currency"
          :expiration-date="getExpirationDate(props.subscription)"
        />
        <BaseButton
          class="font-bold"
          name="submit"
          full-width
          :disabled="loading"
          @click="payWithOwnCreditCard"
        >
          {{ t('labels.purchase') }}
        </BaseButton>
      </template>

      <BillingFormFields
        v-else-if="hasMercadoPagoPayment"
        :submit-label="t('labels.purchase')"
        :outline="props.outline"
        :disabled="loading"
        @submit="card => handlePayment(card)"
      >
        <template #before-submit>
          <div>
            <BaseCheckbox
              v-if="userCreditCard" v-model="shouldSaveNewCard" name="save-card"
              :label="t('pages.account_subscription.checkbox_apply_as_new_credit_card')" class="text-main"
            />
            <AccountSubscriptionBuyDetails
              :price="props.subscription?.price"
              :currency="props.subscription?.currency"
              :expiration-date="getExpirationDate(props.subscription)"
            />
          </div>
        </template>
      </BillingFormFields>

      <slot name="cancel" :disabled="loading" />
    </div>
  </div>
</template>
